import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import {
  Zap, CheckCircle, Users, BarChart3, FileText, Headphones,
  ArrowRight, Clock, Gift, Star, Shield, Sparkles, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EarlyAdopterPage = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/early-adopter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });

      if (response.ok) {
        setRegistered(true);
        toast({
          title: 'Inscription confirmée !',
          description: 'Vous serez notifié dès le lancement du plan Expert.',
        });
      } else {
        const data = await response.json();
        toast({
          title: 'Erreur',
          description: data.detail || 'Une erreur est survenue',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de traiter votre demande',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const expertFeatures = [
    { icon: FileText, text: "Tout le plan Pro inclus", highlight: true },
    { icon: Users, text: "Multi-utilisateurs (3 inclus)" },
    { icon: BarChart3, text: "e-Reporting TVA (disponible sept. 2026)" },
    { icon: FileText, text: "Export comptable CSV" },
    { icon: Shield, text: "Transmission PPF/PDP réelle" },
    { icon: Headphones, text: "Support dédié prioritaire" },
  ];

  return (
    <>
      <Helmet>
        <title>Offre Early Adopter - Plan Expert à 39€ | SmartFacture AI</title>
        <meta name="description" content="Réservez votre place parmi les 100 premiers utilisateurs du plan Expert SmartFacture AI à 39€/mois au lieu de 49€. Offre limitée !" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-indigo-950 text-white">
        {/* Navigation */}
        <nav className="border-b border-white/10 sticky top-0 z-50 bg-slate-900/80 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <span className="text-xl font-bold">SmartFacture AI</span>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  Voir tous les tarifs
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-16 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full mb-8">
              <Gift className="h-5 w-5 text-amber-400" />
              <span className="text-amber-300 font-semibold">Offre limitée aux 100 premiers</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Devenez{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                Early Adopter
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-slate-300 max-w-2xl mx-auto mb-8">
              Accédez au plan Expert en avant-première à{' '}
              <span className="text-white font-bold">39€/mois</span> au lieu de 49€
            </p>

            {/* Price Comparison */}
            <div className="flex justify-center items-center gap-6 mb-12">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Prix normal</p>
                <p className="text-3xl font-bold text-slate-500 line-through">49€</p>
                <p className="text-slate-500 text-sm">/mois</p>
              </div>
              <ArrowRight className="h-8 w-8 text-amber-500" />
              <div className="text-center p-4 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl border border-amber-500/30">
                <p className="text-amber-400 text-sm font-semibold mb-1">Early Adopter</p>
                <p className="text-4xl font-bold text-white">39€</p>
                <p className="text-slate-300 text-sm">/mois</p>
                <p className="text-emerald-400 text-xs mt-1 font-medium">-20% à vie</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              Ce que vous obtenez avec le plan Expert
            </h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {expertFeatures.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 p-4 rounded-xl border ${
                      feature.highlight
                        ? 'bg-blue-500/10 border-blue-500/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      feature.highlight ? 'bg-blue-500/20' : 'bg-white/10'
                    }`}>
                      <Icon className={`h-5 w-5 ${feature.highlight ? 'text-blue-400' : 'text-slate-300'}`} />
                    </div>
                    <span className={feature.highlight ? 'text-white font-medium' : 'text-slate-300'}>
                      {feature.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-3xl border border-white/10 p-8 sm:p-10">
              {!registered ? (
                <>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <Sparkles className="h-6 w-6 text-amber-400" />
                    <h3 className="text-xl font-bold">Réservez votre place</h3>
                  </div>

                  <p className="text-slate-400 text-center mb-6">
                    Laissez votre email pour être notifié dès le lancement et bénéficier du tarif Early Adopter.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                      className="h-12 bg-white/5 border-white/20 text-white placeholder:text-slate-500 rounded-xl"
                      data-testid="early-adopter-email"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl"
                      data-testid="early-adopter-submit"
                    >
                      {loading ? 'Inscription...' : 'Je réserve ma place'}
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </Button>
                  </form>

                  <div className="flex items-center justify-center gap-4 mt-6 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Places limitées
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="h-4 w-4" />
                      Sans engagement
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Inscription confirmée !</h3>
                  <p className="text-slate-400 mb-6">
                    Vous recevrez un email dès que le plan Expert sera disponible.
                  </p>
                  <Link to="/pricing">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                      Voir les plans disponibles
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Questions fréquentes</h2>

            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h4 className="font-semibold mb-2">Quand le plan Expert sera-t-il disponible ?</h4>
                <p className="text-slate-400 text-sm">
                  Nous travaillons activement sur les fonctionnalités avancées. Le lancement est prévu dans les prochaines semaines. Les Early Adopters seront les premiers informés.
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h4 className="font-semibold mb-2">Le tarif de 39€ est-il garanti ?</h4>
                <p className="text-slate-400 text-sm">
                  Oui ! En vous inscrivant maintenant, vous bloquez le tarif Early Adopter de 39€/mois à vie, même après que le prix passe à 49€.
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h4 className="font-semibold mb-2">Puis-je utiliser SmartFacture en attendant ?</h4>
                <p className="text-slate-400 text-sm">
                  Absolument ! Nos plans Starter (14€/mois) et Pro (29€/mois) sont disponibles immédiatement. Vous pourrez facilement upgrader vers Expert quand il sera lancé.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-400 mb-4">
              Vous préférez commencer tout de suite ?
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700 h-12 px-8 rounded-xl">
                  Essayer gratuitement (7 jours)
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 rounded-xl">
                  Voir tous les tarifs
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-slate-500 text-sm">
              © 2026 SmartFacture AI. Tous droits réservés.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default EarlyAdopterPage;
